import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Trust icons
import ShieldIcon from "@mui/icons-material/Shield";
import BadgeIcon from "@mui/icons-material/Badge";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

// Steps icons
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";

const BINANCE_YELLOW = "#F0B90B";
const CARD_BG = "#263238";
const ITEM_BG = "#37474F";
const BORDER_COLOR = "rgba(255,255,255,0.08)";
const TEXT_MUTED = "#B0BEC5";

const trustItems = [
  {
    icon: <ShieldIcon />,
    title: "Security-First",
    text: "Encryption, 2FA, withdrawal allow-lists, continuous monitoring.",
    color: "#42A5F5", // أزرق أمان
  },
  {
    icon: <BadgeIcon />,
    title: "Compliance-Ready",
    text: "AML/KYC best practices, transparent fees & reporting.",
    color: "#26A69A", // فيروزي/أخضر امتثال
  },
  {
    icon: <WorkspacePremiumIcon />,
    title: "Institutional-grade",
    text: "Robust infrastructure, scalable APIs, real-time data.",
    color: "#FFC107", // ذهبي للجودة المؤسسية
  },
  {
    icon: <LockPersonIcon />,
    title: "Privacy-Driven",
    text: "Minimal data, cookie controls, GDPR-friendly principles.",
    color: "#AB47BC", // بنفسجي للخصوصية
  },
];

// غلاف أيقونة بخلفية داكنة ولمسة صفراء بسيطة + توهج بلون العلامة
function IconWrap({ children, brandColor = BINANCE_YELLOW }) {
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "999px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: ITEM_BG,
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${BORDER_COLOR}`,
        boxShadow: `0 0 0 2px rgba(240,185,11,0.15)`,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(240,185,11,0.22), transparent 45%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          color: brandColor,
          filter: `drop-shadow(0 0 4px ${brandColor}66)`,
          "& svg": { fontSize: 26 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function TrustAndSteps() {
  const navigate = useNavigate();

  const stepsItems = [
    {
      icon: <PersonAddAltIcon />,
      title: "1) Create your account",
      desc: "Sign up in seconds with your email.",
      color: "#00BCD4", // سماوي للبدء/الانضمام
      clickable: true,
      onClick: () => navigate("/register"),
    },
    {
      icon: <VerifiedUserIcon />,
      title: "2) Secure & verify",
      desc: "Enable 2FA and complete KYC for enhanced protection.",
      color: "#66BB6A", // أخضر للتحقق/الأمان
      clickable: false,
    },
    {
      icon: <CandlestickChartIcon />,
      title: "3) Start trading",
      desc: "Deposit, select a pair, place your order.",
      color: "#FF9800", // برتقالي للتداول/النشاط
      clickable: false,
    },
  ];

  return (
    <Card
      sx={{
        bgcolor: CARD_BG,
        borderRadius: 2,
        border: `1px solid ${BORDER_COLOR}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        {/* عنوان رئيسي للقسم المدمج */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 900, color: "#FFFFFF", letterSpacing: 0.2 }}
          >
            Trust & Getting Started
          </Typography>
          <Chip
            size="small"
            label="Security • Compliance • Quick Onboarding"
            sx={{
              bgcolor: "rgba(240,185,11,0.12)",
              color: BINANCE_YELLOW,
              border: "1px solid rgba(240,185,11,0.25)",
              fontWeight: 700,
            }}
          />
        </Box>

        <Typography sx={{ color: TEXT_MUTED, mb: 2 }}>
          We combine institutional-grade security with a streamlined onboarding
          so you can start trading confidently in minutes.
        </Typography>

        <Grid container spacing={2.5}>
          {/* عمود الثقة */}
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                bgcolor: CARD_BG,
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFFFFF" }}
                  >
                    Why 1CryptoX is Trusted
                  </Typography>
                  <Box
                    sx={{
                      height: 3,
                      width: 24,
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${BINANCE_YELLOW}, rgba(240,185,11,0.25))`,
                    }}
                  />
                </Box>

                <Grid container spacing={2}>
                  {trustItems.map((it, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="flex-start"
                      >
                        <IconWrap brandColor={it.color}>{it.icon}</IconWrap>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: "#FFFFFF",
                              lineHeight: 1.2,
                            }}
                          >
                            {it.title}
                          </Typography>
                          <Typography sx={{ color: TEXT_MUTED, mt: 0.5 }}>
                            {it.text}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* فاصل عمودي على الشاشات الكبيرة فقط */}
          <Grid
            item
            md={0.5}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "stretch",
              justifyContent: "center",
            }}
          >
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: BORDER_COLOR, opacity: 0.6 }}
            />
          </Grid>

          {/* عمود الخطوات */}
          <Grid item xs={12} md={4.5}>
            <Card
              sx={{
                bgcolor: CARD_BG,
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFFFFF" }}
                  >
                    Getting Started in 3 Steps
                  </Typography>
                  <Box
                    sx={{
                      height: 3,
                      width: 24,
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${BINANCE_YELLOW}, rgba(240,185,11,0.25))`,
                    }}
                  />
                </Box>

                <Stack spacing={1.5}>
                  {stepsItems.map((it, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      spacing={1.25}
                      alignItems="flex-start"
                      onClick={it.onClick}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: ITEM_BG,
                        border: `1px solid ${BORDER_COLOR}`,
                        cursor: it.clickable ? "pointer" : "default",
                        transition: "all 0.25s ease",
                        "&:hover": it.clickable
                          ? {
                              borderColor: BINANCE_YELLOW,
                              bgcolor: "rgba(240,185,11,0.08)",
                              boxShadow: "0 0 10px rgba(240,185,11,0.2)",
                              transform: "translateY(-2px)",
                            }
                          : undefined,
                      }}
                    >
                      <IconWrap brandColor={it.color}>{it.icon}</IconWrap>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: "#FFFFFF",
                            lineHeight: 1.2,
                          }}
                        >
                          {it.title}
                        </Typography>
                        <Typography sx={{ color: TEXT_MUTED, mt: 0.25 }}>
                          {it.desc}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
